<script setup>
import {
    IconHome,
    IconPlus,
    IconWand,
    IconHistory,
    IconUser,
    IconLogout,
    IconChevronDown,
    IconLayoutDashboard
} from '@tabler/icons-vue';
import axios from 'axios';
import { provide } from 'vue';
import Technicals from "./technicals.vue";

const nuxtApp = useNuxtApp();

// Глобальный перехват ошибок загрузки чанков (когда вышел новый релиз)
nuxtApp.hook('app:chunkError', (error) => {
    // Принудительно перезагружаем страницу без использования кэша
    window.location.reload(true);
});

nuxtApp.hook('vue:error', (err) => {
    console.log(err)
    if (err.message && err.message.includes('Failed to fetch dynamically imported module')) {
        // Очищаем кэш и перезагружаем страницу, чтобы получить новые скрипты
        window.location.reload(true);
    }
});

// @todo: Заглушка версии
const version = 'v0.4.6';

const router = useRouter();
const route = useRoute(); // Инициализируем route для проверки meta полей
// В реальном приложении эти данные лучше брать из стора (Pinia) или useFetch
const props = defineProps({
    userPhone: String,
    userWallet: { type: [Number, String], default: 0 },
    userAccess: { type: [Number, String], default: 0 },
    fileQuota: { type: String, default: '' }
});

const isAuthenticated = ref(false);
const hasAccess = computed(() => isAuthenticated.value && Number(props.userAccess) === 1);
const hasBalance = computed(() => Number(props.userWallet) >= 1);
const currentProjectId = ref(null);
const currentSubdomain = ref(null);
const showUserDropdown = ref(false);
const userWallet = ref(null);
const useGeminiKey = ref(0);
const currentUser = ref({
    wallet: 0, // Инициализация кошелька
    access: 0, // Инициализация доступа (0 или 1)
    isLoaded: false, // Флаг для отслеживания загрузки
    phone: null, // Инициализация телефона
    tariff: 0,
});

provide('currentUser', currentUser);
provide('isAuthenticated', isAuthenticated);

const logout = () => {
    localStorage.removeItem('authToken');
    isAuthenticated.value = false;
    showUserDropdown.value = false;
    window.href.reload()
};

// Список защищенных страниц (поддерживает маски с *)
const protectedRoutes = [
    '/projects',
    '/profile',
    'projects/chat/*',
];

// Функция проверки совпадения пути с маской
const isPathProtected = (targetPath, patterns) => {
    return patterns.some(pattern => {
        const normalizedPattern = pattern.startsWith('/') ? pattern : '/' + pattern;

        if (normalizedPattern.includes('*')) {
            const regexString = '^' + normalizedPattern
                .replace(/[.+^${}()|[\]\\]/g, '\\$&')
                .replace(/\\\*/g, '.*') + '$';
            return new RegExp(regexString).test(targetPath);
        }

        return targetPath === normalizedPattern || targetPath.startsWith(normalizedPattern + '/');
    });
};

// Единая функция проверки авторизации
const checkAuthAndRedirect = () => {
    if (typeof window === 'undefined') return;

    const tokenExists = !!localStorage.getItem('authToken');
    isAuthenticated.value = tokenExists;

    // Если страница защищена, а токена нет — отправляем на /login
    if (isPathProtected(route.path, protectedRoutes) && !tokenExists) {
        router.push('/login');
    }
};

// 1. Проверяем сразу при монтировании компонента (для прямых заходов по URL / F5)
onMounted(() => {
    checkAuthAndRedirect();
    fetchCurrentProject();
    fetchUserData();

    document.addEventListener('click', (e) => {
        if (!e.target.closest('.user-dropdown-container')) {
            showUserDropdown.value = false;
        }
    });
});

// 2. Проверяем при смене URL во время кликов по сайту
watch(() => route.path, () => {
    checkAuthAndRedirect();
});

// Модальные окна
const showImprovementModal = ref(false);
const showAnalysisModal = ref(false);
const showVersionHistoryModal = ref(false);

const analysisData = ref({ description: '', files: [], task: '' });

const openImprovementModal = () => showImprovementModal.value = true;
const closeImprovementModal = () => showImprovementModal.value = false;

const handleAnalysisResult = (description, files, task) => {
    analysisData.value = { description, files, task };
    showAnalysisModal.value = true;
};
const closeAnalysisModal = () => showAnalysisModal.value = false;

const openVersionHistoryModal = () => showVersionHistoryModal.value = true;
const closeVersionHistoryModal = () => showVersionHistoryModal.value = false;

// Логика проекта
const fetchCurrentProject = async () => {
    try {
        const token = localStorage.getItem('authToken');
        if (!token) return;

        const response = await axios.get('/api/projects', {
            headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.projects && response.data.projects.length > 0) {
            currentProjectId.value = response.data.projects[0].id;
            currentSubdomain.value = response.data.projects[0].subdomain;
        }
    } catch (error) {
        console.error('Failed to fetch project ID:', error);
    }
};

const fetchUserData = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
        currentUser.value.isLoaded = true; // Считаем загруженным, но пустым
        userWallet.value = null;
        useGeminiKey.value = 0;
        return;
    }

    try {
        const response = await axios.get('/api/user', {
            headers: { Authorization: `Bearer ${token}` }
        });

        // 1. Сохраняем данные для передачи в router-view
        const walletValue = parseFloat(response.data.wallet) || 0;
        currentUser.value.wallet = walletValue;
        currentUser.value.access = response.data.access === 1 ? 1 : 0;
        currentUser.value.tariff = response.data.tariff;
        currentUser.value.isLoaded = true;
        currentUser.value.file_quota = response.data.file_quota+' ГБ';
        currentUser.value.phone = response.data.phone;

        // 2. Обновляем поле для отображения в шапке
        userWallet.value = walletValue.toFixed(2);
        useGeminiKey.value = response.data.use_gemini_key === 1 ? 1 : 0;

    } catch (error) {
        console.error('Failed to fetch user data:', error);
        currentUser.value.isLoaded = true;
        userWallet.value = '—'; // Индикация ошибки загрузки
    }
};
const handleLayoutUpdate = (data) => {
    currentUser.value.wallet = data.wallet;
    currentUser.value.access = data.access;
    userWallet.value = data.wallet.toFixed(2);

};

const support = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
        currentUser.value.isLoaded = true; // Считаем загруженным, но пустым
        userWallet.value = null;
        return;
    }
    const response = await axios.post('/api/support',{}, {
        headers: { Authorization: `Bearer ${token}` }
    });
}

// const isPageReady = computed(() => {
//   // Если маршрут не требует авторизации (Лендинг, Логин), рендерим сразу
//   if (!route.meta.requiresAuth) {
//     return true;
//   }
//   // Если маршрут требует авторизации (Проекты), ждем загрузки данных пользователя
//   return currentUser.value.isLoaded;
// });
const isPageReady = computed(() => {
    // 1. Если мы на главной (лендинг), показываем сразу
    if (route.path === '/') return true;
    if (route.path === '/showcase') return true;
    if (route.path === '/agreement') return true;
    if (route.path === '/oferta') return true;
    if (route.path === '/blog') return true;
    if (route.path.startsWith('/blog/')) return true;
    console.log(currentUser.value)
    // 4. Только в остальных случаях (кабинет) ждем загрузки данных
    return currentUser.value.isLoaded;
});
onMounted(() => {
    isAuthenticated.value = !!localStorage.getItem('authToken');
    fetchCurrentProject();
    fetchUserData();
    document.addEventListener('click', (e) => {
        if (!e.target.closest('.user-dropdown-container')) {
            showUserDropdown.value = false;
        }
    });
});
</script>

<template>
    <VitePwaManifest />
    <div class="flex flex-col min-h-screen bg-[#0f1012] font-sans text-gray-200 selection:bg-indigo-500/30 selection:text-indigo-200">
        <!-- Header -->
        <header v-if="route.path === '/'" class="sticky top-0 z-50 bg-[#0f1012]/80 backdrop-blur-md border-b border-gray-800/60 shadow-sm">
            <nav class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">

                <!-- Logo & Version -->
                <div class="flex items-center gap-3">
                    <router-link to="/" class="flex items-center gap-2 group">
                        <div class="w-8 h-8 rounded-lg bg-gradient-to-tr from-indigo-600 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/20 group-hover:shadow-indigo-500/40 transition-all">
                            <span class="text-white font-bold text-lg">G</span>
                        </div>
                        <span class="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-400">
                            Gefest AI
                        </span>
                    </router-link>
                </div>

                <!-- Navigation -->
                <div class="flex items-center gap-2 sm:gap-4">
                    <template v-if="!isAuthenticated">
                        <a href="https://t.me/gefestai?direct" class="nav-item" target="_blank">
                            <IconWand class="w-4 h-4 text-purple-400" />
                            <span>Поддержка</span>
                        </a>
                        <router-link to="/login" class="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors">
                            Войти
                        </router-link>
                        <!--            <router-link to="/register" class="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-500 rounded-lg shadow-lg shadow-indigo-500/20 transition-all hover:-translate-y-0.5">-->
                        <!--              Регистрация-->
                        <!--            </router-link>-->
                    </template>

                    <template v-else>
                        <div class="hidden md:flex items-center gap-1 pr-4 border-r border-gray-800">
                            <router-link to="/kotel" class="nav-item" active-class="active">
                                <IconLayoutDashboard class="w-4 h-4" />
                                <span>Проекты</span>

                            </router-link>
                            <a href="https://t.me/gefestai?direct" class="nav-item" target="_blank">
                                <IconWand class="w-4 h-4 text-purple-400" />
                                <span>Поддержка</span>
                            </a>
                        </div>

                        <div class="relative user-dropdown-container ml-2">
                            <button
                                @click.stop="showUserDropdown = !showUserDropdown"
                                class="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-gray-800/50 border border-transparent hover:border-gray-700 transition-all"
                            >
                                <div class="h-8 w-8 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 border border-gray-600 flex items-center justify-center text-gray-300">
                                    <IconUser class="w-4 h-4" />
                                </div>
                                <IconChevronDown class="w-3 h-3 text-gray-500 transition-transform duration-200" :class="{ 'rotate-180': showUserDropdown }" />
                            </button>

                            <transition
                                enter-active-class="transition ease-out duration-100"
                                enter-from-class="transform opacity-0 scale-95 -translate-y-2"
                                enter-to-class="transform opacity-100 scale-100 translate-y-0"
                                leave-active-class="transition ease-in duration-75"
                                leave-from-class="transform opacity-100 scale-100 translate-y-0"
                                leave-to-class="transform opacity-0 scale-95 -translate-y-2"
                            >
                                <div v-if="showUserDropdown" class="absolute right-0 mt-2 w-56 rounded-xl shadow-2xl bg-[#1c1e21] border border-gray-700 ring-1 ring-black ring-opacity-5 py-2 overflow-hidden z-50">
                                    <div class="px-4 py-2 border-b border-gray-700/50 mb-1">
                                        <p class="text-xs text-gray-500 uppercase font-semibold tracking-wider">Аккаунт</p>
                                    </div>

                                    <router-link to="/kotel" @click="showUserDropdown = false" class="flex items-center px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-700/50 hover:text-white transition-colors">
                                        <IconUser class="w-4 h-4 mr-2 text-gray-400" />
                                        Личный кабинет
                                    </router-link>

                                    <router-link to="/kotel" @click="showUserDropdown = false" class="flex items-center px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-700/50 hover:text-white transition-colors md:hidden">
                                        <IconLayoutDashboard class="w-4 h-4 mr-2 text-gray-400" />
                                        Проекты
                                    </router-link>

                                    <router-link to="/kotel" @click="showUserDropdown = false" class="flex items-center px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-700/50 hover:text-white transition-colors md:hidden">
                                        <IconPlus class="w-4 h-4 mr-2 text-gray-400" />
                                        Создать проект
                                    </router-link>

                                    <div class="h-px bg-gray-700/50 my-1 mx-2"></div>

                                    <button @click="logout" class="w-full flex items-center px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors text-left">
                                        <IconLogout class="w-4 h-4 mr-2" />
                                        Выйти
                                    </button>
                                </div>
                            </transition>
                        </div>
                    </template>
                </div>
            </nav>
        </header>
        <main v-if="isPageReady" class="flex-grow">
            <slot v-if="$slots.default" />
            <NuxtPage v-else
                      :user-wallet="currentUser.wallet"
                      :user-access="currentUser.access"
                      :user-phone="currentUser.phone"
                      :file-quota="currentUser.file_quota"
                      :user-tariff="currentUser.tariff"
                      @layout-update="handleLayoutUpdate"
            />
<!--            <technicals></technicals>-->
        </main>

        <!-- Лоадер показывается только если мы на защищенной странице и данные еще грузятся -->
        <div v-else class="flex-grow flex items-center justify-center relative z-0">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
    </div>
</template>
<style scoped>
.nav-item {
    @apply flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-400 hover:text-white hover:bg-gray-800/50 transition-all duration-200;
}
.nav-item.active {
    @apply text-white bg-gray-800;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease-in-out;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
